import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Scale, RefreshCw, Search, Package, TrendingDown, TrendingUp, Store, X, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';

// Propia price tracking: own site vs pharmacy reseller, competitor equivalents, daily price log.
// Data: /api/propia-prices (scripts/scrape_propia_prices.py, daily).

const API_BASE = 'https://api.glowytics.com';
const OWN = 'Propia';

const fmt = (v) =>
  v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtUnit = (v, u) => (v == null ? '—' : `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}/${u || '?'}`);
const fmtSize = (v, u) => (v == null ? '' : `${Number(v).toLocaleString('tr-TR')} ${u === 'adet' ? 'pcs' : u}`);
const pct = (a, b) => (a == null || b == null || !b ? null : ((a - b) / b) * 100);
const SOURCE_LABEL = {
  'propia.com': 'propia.com', 'dermoeczanem.com': 'Dermoeczanem', 'thepurestsolutions.com': 'The Purest Solutions',
  'creamco.com.tr': 'Cream Co.', 'dermokil.com': 'Dermokil', 'procsin.com': 'Procsin', 'siveno.com': 'Siveno',
};
const TYPE_EN = {
  sivilce_bandi: 'Pimple patch', dudak: 'Lip care', goz_kremi: 'Eye cream', gunes: 'Sunscreen',
  sac_sampuan: 'Shampoo', sac_kremi: 'Conditioner', sac_serum: 'Hair serum', yuz_maskesi: 'Face mask',
  yuz_temizleme: 'Face cleanser', tonik: 'Toner', serum: 'Face serum', nemlendirici: 'Moisturiser',
  set: 'Set / kit', diger: 'Other',
};

function StatCard({ icon: Icon, label, value, sub, color = 'text-indigo-600 bg-indigo-50', valueColor = 'text-gray-900' }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className={`text-lg font-bold tabular-nums truncate ${valueColor}`}>{value}</div>
        {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function Diff({ value, invert = false, suffix = '%' }) {
  if (value == null || !isFinite(value)) return <span className="text-gray-300">—</span>;
  if (Math.abs(value) < 0.5) return <span className="text-gray-400 tabular-nums">≈</span>;
  // invert=false: positive = rival/reseller more expensive than us = good for us (green)
  const good = invert ? value < 0 : value > 0;
  return (
    <span className={`tabular-nums font-medium ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      {value > 0 ? '+' : ''}{value.toFixed(0)}{suffix}
    </span>
  );
}

function Thumb({ src }) {
  return src
    ? <img src={src} alt="" loading="lazy" className="w-12 h-12 rounded-lg object-cover border border-gray-100 bg-white shrink-0" />
    : <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0" />;
}

export default function PropiaPriceTracking() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';
  const [tab, setTab] = useState('equivalents');
  const [overview, setOverview] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [type, setType] = useState('');
  const [catBrand, setCatBrand] = useState('');
  const [search, setSearch] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [history, setHistory] = useState(null); // { name, source, rows }

  const api = useCallback(async (path) => {
    const res = await fetch(`${API_BASE}/api/propia-prices${path}`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId },
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    return res.json();
  }, [brandId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, c] = await Promise.all([api('/overview'), api('/catalog')]);
      setOverview(o);
      setCatalog(c.rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openHistory = async (r) => {
    setHistory({ name: r.name, source: r.source, url: r.url, rows: null });
    try {
      const h = await api(`/history?url=${encodeURIComponent(r.url)}`);
      setHistory({ name: r.name, source: r.source, url: r.url, rows: h.rows });
    } catch (e) {
      setHistory({ name: r.name, source: r.source, url: r.url, rows: [], error: e.message });
    }
  };

  const products = useMemo(() => overview?.products || [], [overview]);
  const singles = useMemo(() => products.filter((p) => p.ptype !== 'set' && p.ptype !== 'diger'), [products]);
  const types = useMemo(() => [...new Set(singles.map((p) => p.ptype))], [singles]);

  const kpi = useMemo(() => {
    let cheaperRival = 0; let compared = 0; let resellerLower = 0; let resellerN = 0;
    for (const p of singles) {
      const best = p.equivalents.filter((e) => e.rank === 1 && e.unitRatio != null);
      if (best.length) {
        compared += 1;
        if (best.some((e) => e.unitRatio < 0.95)) cheaperRival += 1;
      }
      if (p.reseller?.price && p.price) {
        resellerN += 1;
        if (p.reseller.price < p.price * 0.99) resellerLower += 1;
      }
    }
    const comp = (catalog || []).filter((r) => r.brand !== OWN);
    return { cheaperRival, compared, resellerLower, resellerN, compN: comp.length,
      compBrands: new Set(comp.map((r) => r.brand)).size,
      changed: (catalog || []).filter((r) => r.prev_price != null).length };
  }, [singles, catalog]);

  const shownProducts = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    return products
      .filter((p) => (type ? p.ptype === type : true))
      .filter((p) => (q ? p.name.toLocaleLowerCase('tr').includes(q) : true))
      .sort((a, b) => (a.ptype === 'set') - (b.ptype === 'set') || a.typeLabel.localeCompare(b.typeLabel) || a.name.localeCompare(b.name));
  }, [products, type, search]);

  const catBrands = useMemo(() => [...new Set((catalog || []).map((r) => r.brand))].sort(), [catalog]);
  const shownCatalog = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    return (catalog || [])
      .filter((r) => (catBrand ? r.brand === catBrand : true))
      .filter((r) => (type ? r.ptype === type : true))
      .filter((r) => (onlyChanged ? r.prev_price != null : true))
      .filter((r) => (q ? r.name.toLocaleLowerCase('tr').includes(q) : true));
  }, [catalog, catBrand, type, onlyChanged, search]);

  const dataLine = (overview?.sources || [])
    .map((s) => `${SOURCE_LABEL[s.source] || s.source} ${s.last_date}`).join(' · ');

  if (error) {
    return (
      <div className="p-6 max-w-[1320px] mx-auto">
        <Card className="p-6 text-sm text-red-600">Couldn't load price data: {error}</Card>
      </div>
    );
  }

  const tabs = [
    ['equivalents', 'Equivalent products'],
    ['unit', 'Unit price by category'],
    ['tracking', 'Price log'],
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1320px] mx-auto">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Scale className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Price Tracking</h1>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Propia's prices on propia.com and Dermoeczanem, next to the closest equivalent from each competitor's
                  own store. Equivalents are matched by product type, then size; unit price is per ml, g or piece.
                  Updated daily.
                  {dataLine && <span className="ml-1 text-gray-400">· Data: {dataLine}</span>}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Package} label="Propia products tracked" value={products.length}
              sub={`${singles.length} single products · ${products.length - singles.length} sets`} />
            <StatCard icon={Store} color="text-gray-600 bg-gray-100" label="Competitor products tracked"
              value={kpi.compN.toLocaleString('tr-TR')} sub={`${kpi.compBrands} brands incl. pharmacy listings`} />
            <StatCard icon={TrendingDown} color="text-red-600 bg-red-50" valueColor="text-red-600"
              label="Cheaper equivalent exists" value={`${kpi.cheaperRival}/${kpi.compared}`}
              sub="a rival's closest match costs ≥5% less per unit" />
            <StatCard icon={TrendingUp} color="text-amber-600 bg-amber-50" valueColor="text-amber-600"
              label="Dermoeczanem below propia.com" value={`${kpi.resellerLower}/${kpi.resellerN}`}
              sub="products listed on both" />
          </div>
        </div>

        <div className="px-6 pt-4 bg-white flex flex-wrap items-center gap-2 border-b">
          {tabs.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              {l}
            </button>
          ))}
        </div>

        {tab !== 'unit' && (
          <div className="px-6 py-4 bg-white flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products"
                className="pl-9 pr-3 h-9 w-64 rounded-xl border border-gray-200 text-sm" />
            </div>
            <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 rounded-xl border border-gray-200 text-sm px-2">
              <option value="">All categories</option>
              {(tab === 'tracking' ? Object.keys(TYPE_EN) : types).map((t) => <option key={t} value={t}>{TYPE_EN[t] || t}</option>)}
            </select>
            {tab === 'tracking' && (
              <>
                <select value={catBrand} onChange={(e) => setCatBrand(e.target.value)} className="h-9 rounded-xl border border-gray-200 text-sm px-2">
                  <option value="">All brands</option>
                  {catBrands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-gray-600 ml-1">
                  <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
                  Price changed ({kpi.changed})
                </label>
              </>
            )}
          </div>
        )}

        <div className="bg-white">
          {loading && !overview && <div className="p-10 text-center text-sm text-gray-400">Loading…</div>}

          {tab === 'equivalents' && overview && (
            <div className="divide-y divide-gray-100">
              {shownProducts.map((p) => (
                <div key={p.url} className="px-6 py-4 grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4">
                  <div className="flex gap-3 min-w-0">
                    <Thumb src={p.image} />
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">{TYPE_EN[p.ptype] || p.ptype}{p.size_value ? ` · ${fmtSize(p.size_value, p.size_unit)}` : ''}</div>
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-gray-900 hover:text-indigo-700 line-clamp-2">{p.name}</a>
                      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                        <button type="button" onClick={() => openHistory(p)} className="font-bold tabular-nums text-gray-900 hover:underline">{fmt(p.price)}</button>
                        {p.unit_price != null && <span className="text-xs text-gray-500 tabular-nums">{fmtUnit(p.unit_price, p.size_unit)}</span>}
                        {!p.in_stock && <span className="text-xs text-red-600">Out of stock</span>}
                      </div>
                      {p.reseller && (
                        <div className="mt-1 text-xs text-gray-500">
                          <a href={p.reseller.url} target="_blank" rel="noreferrer" className="hover:underline">Dermoeczanem</a>{' '}
                          <button type="button" onClick={() => openHistory({ ...p.reseller, name: p.reseller.name })} className="tabular-nums text-gray-800 hover:underline">{fmt(p.reseller.price)}</button>{' '}
                          <Diff value={pct(p.reseller.price, p.price)} />
                          {p.reseller.stock_qty != null && <span className="text-gray-400"> · {p.reseller.stock_qty} in stock</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="min-w-0">
                    {p.ptype === 'set' || p.ptype === 'diger' ? (
                      <div className="text-xs text-gray-400 pt-1">Sets and bundles aren't matched to equivalents.</div>
                    ) : p.equivalents.length === 0 ? (
                      <div className="text-xs text-gray-400 pt-1">No competitor sells this product type in the tracked stores.</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
                        {p.equivalents.filter((e) => e.rank === 1).map((e) => (
                          <div key={e.comp_url} className="rounded-xl border border-gray-100 p-2.5 flex gap-2.5 min-w-0">
                            <Thumb src={e.comp_image} />
                            <div className="min-w-0 flex-1">
                              <div className="text-[11px] font-semibold text-gray-500">{e.comp_brand}</div>
                              <a href={e.comp_url} target="_blank" rel="noreferrer" className="text-xs text-gray-800 hover:text-indigo-700 line-clamp-2">{e.comp_name}</a>
                              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
                                <button type="button" onClick={() => openHistory({ url: e.comp_url, name: e.comp_name, source: e.comp_source })} className="font-semibold tabular-nums text-gray-900 hover:underline">{fmt(e.comp_price)}</button>
                                {e.comp_size != null && <span className="text-gray-400">{fmtSize(e.comp_size, p.size_unit)}</span>}
                                <span className="text-gray-400">·</span>
                                <span title="Rival's price per unit vs ours"><Diff value={e.unitRatio == null ? null : (e.unitRatio - 1) * 100} /> <span className="text-gray-400">/unit</span></span>
                                {e.comp_in_stock === false && <span className="text-red-500">out of stock</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {shownProducts.length === 0 && <div className="p-10 text-center text-sm text-gray-400">No products match the filter.</div>}
            </div>
          )}

          {tab === 'unit' && overview && (
            <div className="p-6 overflow-x-auto">
              <p className="text-xs text-gray-500 mb-3">
                Median price per unit of each brand's single products in the category, from the brand's own store.
                Cells show how much more (red = cheaper than Propia) the brand charges per unit; n = number of products.
              </p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-4 font-medium">Category</th>
                    {overview.brands.map((b) => <th key={b} className="py-2 px-3 font-medium text-right whitespace-nowrap">{b}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {overview.typeSummary.map((t) => {
                    const own = t.cells[OWN]?.median;
                    return (
                      <tr key={t.ptype} className="border-b border-gray-50">
                        <td className="py-2 pr-4 font-medium text-gray-800 whitespace-nowrap">{TYPE_EN[t.ptype] || t.label} <span className="text-gray-400 font-normal">per {t.unit === 'adet' ? 'piece' : t.unit}</span></td>
                        {overview.brands.map((b) => {
                          const c = t.cells[b];
                          return (
                            <td key={b} className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${b === OWN ? 'bg-indigo-50/50' : ''}`}>
                              {c ? (
                                <>
                                  <div className="text-gray-900">{fmtUnit(c.median, t.unit)}</div>
                                  <div className="text-[11px] text-gray-400">
                                    {b !== OWN && own ? <Diff value={pct(c.median, own)} /> : null} n={c.n}
                                  </div>
                                </>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'tracking' && catalog && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pl-6 pr-3 font-medium">Product</th>
                    <th className="py-2 px-3 font-medium">Store</th>
                    <th className="py-2 px-3 font-medium">Category</th>
                    <th className="py-2 px-3 font-medium text-right">Price</th>
                    <th className="py-2 px-3 font-medium text-right">Unit price</th>
                    <th className="py-2 px-3 font-medium text-right">vs 7 days ago</th>
                    <th className="py-2 px-3 font-medium text-right">Last change</th>
                    <th className="py-2 pl-3 pr-6 font-medium">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {shownCatalog.slice(0, 600).map((r) => (
                    <tr key={`${r.source}|${r.url}`} className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${r.brand === OWN ? 'bg-indigo-50/30' : ''}`} onClick={() => openHistory(r)}>
                      <td className="py-2 pl-6 pr-3">
                        <div className="flex items-center gap-2 min-w-[280px]">
                          {r.image ? <img src={r.image} alt="" loading="lazy" className="w-8 h-8 rounded object-cover border border-gray-100" /> : <div className="w-8 h-8 rounded bg-gray-100" />}
                          <div className="min-w-0">
                            <div className="text-[11px] text-gray-400">{r.brand}</div>
                            <div className="text-gray-800 line-clamp-1">{r.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{SOURCE_LABEL[r.source] || r.source}</td>
                      <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{TYPE_EN[r.ptype] || r.ptype}</td>
                      <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">
                        <span className="text-gray-900">{fmt(r.price)}</span>
                        {r.list_price && <span className="ml-1 text-xs text-gray-400 line-through">{fmt(r.list_price)}</span>}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-gray-500 whitespace-nowrap">{r.unit_price ? fmtUnit(r.unit_price, r.size_unit) : '—'}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap"><Diff value={pct(r.price, r.price_7d)} invert /></td>
                      <td className="py-2 px-3 text-right text-xs text-gray-500 whitespace-nowrap">
                        {r.prev_price != null ? <>{fmt(r.prev_price)} → {fmt(r.price)} <span className="text-gray-400">({r.prev_date})</span></> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-2 pl-3 pr-6 whitespace-nowrap text-xs">
                        {r.in_stock ? <span className="text-emerald-600">In stock{r.stock_qty != null ? ` · ${r.stock_qty}` : ''}</span> : <span className="text-red-600">Out</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 text-xs text-gray-400">
                {shownCatalog.length > 600 ? `Showing 600 of ${shownCatalog.length}. Narrow with the filters.` : `${shownCatalog.length} products.`}
                {' '}Price history starts on the first day of tracking; "vs 7 days ago" fills in after a week.
              </div>
            </div>
          )}
        </div>
      </Card>

      {history && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setHistory(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-gray-400">{SOURCE_LABEL[history.source] || history.source}</div>
                <div className="text-sm font-semibold text-gray-900">{history.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <a href={history.url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-700" title="Open product"><ExternalLink className="w-4 h-4" /></a>
                <button type="button" onClick={() => setHistory(null)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="mt-4 h-64">
              {!history.rows ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-400">Loading…</div>
              ) : history.rows.length < 2 ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-400 text-center px-6">
                  {history.rows.length === 1
                    ? `One day of data so far (${history.rows[0].date}: ${fmt(history.rows[0].price)}). The chart fills in as daily prices accumulate.`
                    : 'No price history.'}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history.rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => `₺${v}`} domain={['auto', 'auto']} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Line type="stepAfter" dataKey="price" name="Price" stroke="#4f46e5" strokeWidth={2} dot={false} />
                    <Line type="stepAfter" dataKey="list_price" name="List price" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
